import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, Mail, Sparkles, RotateCcw, Save, Send, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { adminFetch, adminPost } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function adminPut(path, pw, body) {
  const res = await fetch(`${API}${path}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", "X-Admin-Password": pw },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error((await res.json()).detail || "Update failed");
  return res.json();
}

async function adminDelete(path, pw) {
  const res = await fetch(`${API}${path}`, {
    method: "DELETE",
    headers: { "X-Admin-Password": pw },
  });
  if (!res.ok) throw new Error((await res.json()).detail || "Reset failed");
  return res.json();
}

export default function EmailTemplates({ pw }) {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // current template key
  const [editor, setEditor] = useState(null); // { key, subject, preview, body_html, cta_label, wrap_title, auto_cta, default_*, is_override }
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminFetch("/admin/email-templates", pw);
      setTemplates(data.templates || []);
    } catch (e) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const t of templates) {
      (g[t.group] ||= []).push(t);
    }
    return g;
  }, [templates]);

  const openEditor = async (key) => {
    try {
      const full = await adminFetch(`/admin/email-templates/${key}`, pw);
      setEditor(full);
      setEditing(key);
      setPreviewHtml("");
    } catch (e) {
      toast.error("Failed to load template");
    }
  };

  const closeEditor = () => {
    setEditing(null);
    setEditor(null);
    setPreviewHtml("");
  };

  const updateField = (field, value) => setEditor((s) => ({ ...s, [field]: value }));

  const save = async () => {
    if (!editor) return;
    setSaving(true);
    try {
      const body = {
        subject: editor.subject,
        preview: editor.preview,
        body_html: editor.body_html,
        wrap_title: editor.wrap_title,
      };
      if (editor.auto_cta) body.cta_label = editor.cta_label || "";
      await adminPut(`/admin/email-templates/${editor.key}`, pw, body);
      toast.success("Saved. Next send uses your version.");
      await load();
      // re-open to refresh is_override flag
      const refreshed = await adminFetch(`/admin/email-templates/${editor.key}`, pw);
      setEditor(refreshed);
    } catch (e) {
      toast.error(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!editor) return;
    if (!window.confirm("Reset this email to the original copy? Your edits will be lost.")) return;
    setResetting(true);
    try {
      await adminDelete(`/admin/email-templates/${editor.key}`, pw);
      toast.success("Reset to default copy.");
      await load();
      const refreshed = await adminFetch(`/admin/email-templates/${editor.key}`, pw);
      setEditor(refreshed);
      setPreviewHtml("");
    } catch (e) {
      toast.error(e.message || "Failed to reset");
    } finally {
      setResetting(false);
    }
  };

  const sendTest = async () => {
    if (!editor || !testEmail) {
      toast.error("Enter a test email address.");
      return;
    }
    setSendingTest(true);
    try {
      // Save unsaved edits first so the test reflects what you see.
      await save();
      const res = await adminPost(`/admin/email-templates/${editor.key}/test`, pw, {
        to_email: testEmail,
        first_name: "Donja",
      });
      toast.success(res?.sent ? `Test sent to ${testEmail}.` : "Sender is mocked (no Resend key). Configure RESEND_API_KEY to send real emails.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed to send test");
    } finally {
      setSendingTest(false);
    }
  };

  const loadPreview = async () => {
    if (!editor) return;
    setPreviewLoading(true);
    try {
      // Save first so the preview reflects current edits
      if (hasUnsavedEdits()) await save();
      const res = await fetch(`${API}/admin/email-templates/${editor.key}/preview?first=Donja`, {
        headers: { "X-Admin-Password": pw },
      });
      const data = await res.json();
      setPreviewHtml(data.html || "");
    } catch (e) {
      toast.error("Failed to render preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const hasUnsavedEdits = () => {
    if (!editor) return false;
    return (
      editor.subject !== (editor._snapshot?.subject ?? editor.subject)
      || editor.preview !== (editor._snapshot?.preview ?? editor.preview)
      || editor.body_html !== (editor._snapshot?.body_html ?? editor.body_html)
      || editor.wrap_title !== (editor._snapshot?.wrap_title ?? editor.wrap_title)
      || (editor.auto_cta && editor.cta_label !== (editor._snapshot?.cta_label ?? editor.cta_label))
    );
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-ink/50">
        <Loader2 className="animate-spin inline-block mr-2" size={16} /> Loading templates...
      </div>
    );
  }

  return (
    <div data-testid="email-templates-tab" className="space-y-6">
      <div className="flex items-start gap-3 p-4 rounded-2xl border border-brand/30 bg-brand-soft">
        <Sparkles size={16} className="text-brand mt-0.5 shrink-0" />
        <div className="text-sm text-ink/85 leading-relaxed">
          Edit subject, preview, and body for every email the funnel sends. Click any row to open the editor.
          Use the placeholder <code className="px-1 py-0.5 bg-white rounded text-xs">{"{first}"}</code> in the body — it&apos;s replaced with the recipient&apos;s first name on send.
          Templates marked <em>Auto-CTA</em> get the &ldquo;Save My Seat&rdquo; button, scarcity line, and contact email appended automatically; you just write the persuasive copy.
        </div>
      </div>

      {Object.entries(grouped).map(([group, items]) => (
        <div key={group} data-testid={`email-group-${group.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <div className="eyebrow mb-3">{group}</div>
          <div className="rounded-2xl border border-[var(--line)] overflow-hidden bg-bone">
            <ul>
              {items.map((t, i) => (
                <li
                  key={t.key}
                  data-testid={`email-row-${t.key}`}
                  className={`px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-white transition-colors ${i < items.length - 1 ? "border-b border-[var(--line)]" : ""}`}
                  onClick={() => openEditor(t.key)}
                >
                  <Mail size={15} className="text-brand shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-ink truncate">{t.label}</span>
                      {t.is_override && (
                        <Badge variant="outline" className="border-brand/40 text-brand bg-brand-soft text-[10px] px-1.5 py-0">Edited</Badge>
                      )}
                      {t.auto_cta && (
                        <Badge variant="outline" className="border-ink/15 text-ink/55 bg-white text-[10px] px-1.5 py-0">Auto-CTA</Badge>
                      )}
                    </div>
                    <div className="text-sm text-ink/70 truncate mt-0.5">{t.subject}</div>
                  </div>
                  <Button size="sm" variant="outline" className="btn-outline-ink rounded-full h-8 px-3 text-xs shrink-0">Edit</Button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}

      <Dialog open={!!editing} onOpenChange={(o) => !o && closeEditor()}>
        <DialogContent className="bg-white border border-[var(--line)] text-ink max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="email-editor-dialog">
          {editor && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl tracking-[-0.025em] flex items-center gap-2 flex-wrap">
                  {editor.label || editor.key}
                  {editor.is_override && (
                    <Badge variant="outline" className="border-brand/40 text-brand bg-brand-soft text-[10px] px-1.5 py-0">Edited</Badge>
                  )}
                </DialogTitle>
                <DialogDescription className="text-ink/65">
                  {editor.group}
                  {editor.auto_cta && (
                    <span className="ml-2 text-xs text-ink/55">· Save My Seat button + scarcity + contact line are auto-appended.</span>
                  )}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 pt-2">
                <div>
                  <Label htmlFor="et_subject" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono">Subject *</Label>
                  <Input
                    id="et_subject"
                    data-testid="email-editor-subject"
                    value={editor.subject || ""}
                    onChange={(e) => updateField("subject", e.target.value)}
                    className="bg-bone border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
                  />
                </div>
                <div>
                  <Label htmlFor="et_preview" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono">Preview text (inbox snippet)</Label>
                  <Input
                    id="et_preview"
                    data-testid="email-editor-preview"
                    value={editor.preview || ""}
                    onChange={(e) => updateField("preview", e.target.value)}
                    className="bg-bone border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="et_wrap" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono">Heading (in email)</Label>
                    <Input
                      id="et_wrap"
                      data-testid="email-editor-wrap-title"
                      value={editor.wrap_title || ""}
                      onChange={(e) => updateField("wrap_title", e.target.value)}
                      className="bg-bone border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
                    />
                  </div>
                  {editor.auto_cta && (
                    <div>
                      <Label htmlFor="et_cta" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono">CTA button label</Label>
                      <Input
                        id="et_cta"
                        data-testid="email-editor-cta-label"
                        value={editor.cta_label || ""}
                        onChange={(e) => updateField("cta_label", e.target.value)}
                        placeholder="Save My Seat — Pay with Stripe"
                        className="bg-bone border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
                      />
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="et_body" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono">Body HTML *</Label>
                  <Textarea
                    id="et_body"
                    data-testid="email-editor-body"
                    value={editor.body_html || ""}
                    onChange={(e) => updateField("body_html", e.target.value)}
                    rows={14}
                    className="bg-bone border border-[var(--line)] text-ink mt-1.5 font-mono text-[13px] leading-relaxed focus-visible:ring-brand focus-visible:ring-offset-0"
                  />
                  <p className="text-xs text-ink/55 mt-1.5 leading-relaxed">
                    Use <code className="px-1 py-0.5 bg-bone rounded">{"{first}"}</code> for the recipient&apos;s first name. Plain HTML is allowed (<code>&lt;p&gt;</code>, <code>&lt;strong&gt;</code>, <code>&lt;ul&gt;</code>, <code>&lt;a&gt;</code>, inline styles). No need to add header/footer &mdash; we wrap it automatically with the Build Lab brand.
                  </p>
                </div>

                {previewHtml && (
                  <div className="rounded-2xl border border-[var(--line)] overflow-hidden bg-white" data-testid="email-preview-frame">
                    <div className="px-4 py-2 bg-bone border-b border-[var(--line)] text-[11px] uppercase tracking-[0.18em] text-ink/55 font-mono-spec flex items-center gap-2">
                      <Eye size={11} /> Live preview · how subscribers will see it
                    </div>
                    <iframe
                      title="email-preview"
                      srcDoc={previewHtml}
                      sandbox=""
                      className="w-full h-[420px] bg-white"
                    />
                  </div>
                )}

                <div className="rounded-2xl border border-[var(--line)] p-4 bg-bone">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono-spec mb-2">Send a test to yourself</div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      data-testid="email-editor-test-email"
                      type="email"
                      placeholder="you@example.com"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      className="bg-white border border-[var(--line)] text-ink h-10 focus-visible:ring-brand focus-visible:ring-offset-0"
                    />
                    <Button
                      type="button"
                      data-testid="email-editor-send-test"
                      onClick={sendTest}
                      disabled={sendingTest || !testEmail}
                      className="btn-ink rounded-full h-10 px-5 shrink-0"
                    >
                      {sendingTest ? <Loader2 className="animate-spin" size={14} /> : <><Send size={13} className="mr-2" /> Send test</>}
                    </Button>
                  </div>
                </div>
              </div>

              <DialogFooter className="pt-4 gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  onClick={loadPreview}
                  disabled={previewLoading}
                  data-testid="email-editor-preview-btn"
                  className="btn-outline-ink rounded-full h-10 px-5"
                >
                  {previewLoading ? <Loader2 className="animate-spin" size={14} /> : <><Eye size={13} className="mr-2" /> Preview</>}
                </Button>
                {editor.is_override && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetToDefault}
                    disabled={resetting}
                    data-testid="email-editor-reset"
                    className="btn-outline-ink rounded-full h-10 px-5"
                  >
                    {resetting ? <Loader2 className="animate-spin" size={14} /> : <><RotateCcw size={13} className="mr-2" /> Reset to default</>}
                  </Button>
                )}
                <div className="flex-1" />
                <Button type="button" variant="outline" onClick={closeEditor} className="btn-outline-ink rounded-full h-10 px-5">
                  Close
                </Button>
                <Button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  data-testid="email-editor-save"
                  className="btn-ink rounded-full h-10 px-5"
                >
                  {saving ? <Loader2 className="animate-spin" size={14} /> : <><Save size={13} className="mr-2" /> Save</>}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
